import { motion } from "framer-motion";
import { Star, Quote, ExternalLink, CheckCircle } from "lucide-react";

const testimonials = [
  {
    name: "Marie L.",
    role: "Consultante en management",
    company: "Paris",
    content: "Mon portfolio m'a permis de décrocher 3 nouveaux clients en une semaine. Le design premium fait vraiment la différence face à un simple profil LinkedIn.",
    rating: 5,
    highlight: "3 clients en 1 semaine",
    avatar: "ML",
    photo: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face",
    verified: true,
  },
  {
    name: "Thomas B.",
    role: "Architecte d'intérieur",
    company: "Lyon",
    content: "Livraison rapide et professionnelle. Le résultat est bluffant, bien au-delà de mes attentes. Mes projets sont enfin mis en valeur comme ils le méritent.",
    rating: 5,
    highlight: "Résultat bluffant",
    avatar: "TB",
    photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face",
    verified: true,
  },
  {
    name: "Sophie M.",
    role: "Photographe professionnelle",
    company: "Toulouse",
    content: "Le meilleur investissement pour mon activité. Mes prospects adorent le dark mode et les animations. J'ai reçu des compliments dès le premier jour.",
    rating: 5,
    highlight: "Meilleur investissement",
    avatar: "SM",
    photo: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face",
    verified: true,
  },
  {
    name: "Lucas D.",
    role: "Coach sportif",
    company: "Bordeaux",
    content: "En tant qu'indépendant, mon portfolio est ma carte de visite. Depuis que j'ai le mien, mon taux de conversion prospects → clients a doublé.",
    rating: 5,
    highlight: "Taux de conversion x2",
    avatar: "LD",
    photo: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop&crop=face",
    verified: true,
  },
  {
    name: "Emma R.",
    role: "Avocate en droit des affaires",
    company: "Marseille",
    content: "Le portfolio est superbe et le SEO fonctionne vraiment. Je suis premier résultat Google sur mon nom + métier. Incroyable.",
    rating: 5,
    highlight: "#1 sur Google",
    avatar: "ER",
    photo: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=80&h=80&fit=crop&crop=face",
    verified: true,
  },
  {
    name: "Karim A.",
    role: "Agent immobilier",
    company: "Nantes",
    content: "J'hésitais avec un template WordPress. Finalement c'est incomparable : ultra rapide, design unique, et tout configuré en 48h. Zéro regret.",
    rating: 5,
    highlight: "Livré en 48h",
    avatar: "KA",
    photo: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face",
    verified: true,
  },
];

const avatarGradients = [
  "from-primary to-accent",
  "from-accent to-primary",
  "from-primary via-accent to-primary",
  "from-accent via-primary to-accent",
  "from-primary to-accent",
  "from-accent to-primary",
];

const SellTestimonials = () => {
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.02] to-transparent" />
      <div className="container mx-auto px-6 relative">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            ⭐ Témoignages vérifiés
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4">
            Ils ont franchi le pas
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            +50 professionnels nous font confiance. Voici leurs retours.
          </p>
          
          {/* Aggregate rating */}
          <motion.div 
            className="flex items-center justify-center gap-3 mt-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-primary text-primary" />
              ))}
            </div>
            <span className="text-lg font-bold">4.9/5</span>
            <span className="text-muted-foreground text-sm">basé sur 50+ avis</span>
          </motion.div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {testimonials.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity blur-xl" />
              <div className="relative p-7 rounded-2xl bg-card/60 backdrop-blur-sm border border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 h-full flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex gap-0.5">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                  </div>
                  <Quote className="w-6 h-6 text-primary/20" />
                </div>

                {/* Content */}
                <p className="text-foreground/90 text-sm leading-relaxed flex-1 mb-4">"{t.content}"</p>
                
                {/* Highlight badge */}
                <span className="inline-flex self-start px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-5">
                  ✨ {t.highlight}
                </span>

                {/* Author */}
                <div className="flex items-center gap-3 pt-4 border-t border-border/30">
                  {t.photo ? (
                    <img
                      src={t.photo}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover shadow-md ring-2 ring-primary/20"
                      loading="lazy"
                    />
                  ) : (
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${avatarGradients[i % avatarGradients.length]} flex items-center justify-center text-primary-foreground font-bold text-sm shadow-md`}>
                      {t.avatar}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="font-semibold text-sm">{t.name}</p>
                      {t.verified && (
                        <CheckCircle className="w-3.5 h-3.5 text-primary" />
                      )}
                    </div>
                    <p className="text-muted-foreground text-xs">{t.role} · {t.company}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SellTestimonials;
